import { useState, useEffect, useRef } from 'react';
import { ENV_CONFIG } from '../config/environment';
import { usePageContext } from 'vike-react/usePageContext';

export interface LocationTrendsData {
  salaryStats?: {
    aggregate: {
      median: { min: number; max: number };
      average: { min: number; max: number };
      min: number;
      max: number;
      percentile25: { min: number; max: number };
      percentile75: { min: number; max: number };
      percentile90: { min: number; max: number };
      sampleSize: number;
      lastUpdated: number;
    };
    breakdowns?: Array<{
      aggregationType: string;
      aggregationKey: string;
      dimensionValue: string;
      median: { min: number; max: number };
      average: { min: number; max: number };
      min: number;
      max: number;
      percentile25: { min: number; max: number };
      percentile75: { min: number; max: number };
      percentile90: { min: number; max: number };
      sampleSize: number;
      lastUpdated: number;
    }>;
  };
  trends?: {
    fieldDistribution: Record<string, number>;
    remotenessDistribution: Record<string, number>;
    languageRequirements: Record<string, number>;
    companyDistribution: Record<string, number>;
    companyJobCount?: number;
  };
  /**
   * Finland-wide averages for comparing location metrics to national averages.
   * Only present when querying a specific location (not Finland itself).
   */
  finlandAverages?: {
    avgSalaryNum?: number;
    avgSalaryFormatted?: string;
    remotePercentage?: number;
    englishLanguagePercentage?: number;
  };
}

export function useLocationTrends(location: string | null, locale: 'en' | 'fi' | 'sv' = 'en', initialData?: LocationTrendsData | null) {
  // Use initialData as initial state, but always fetch fresh data from API
  const [data, setData] = useState<LocationTrendsData | null>(initialData || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const prevLocationRef = useRef<string | null>(location);
  const prevLocaleRef = useRef<'en' | 'fi' | 'sv'>(locale);
  
  // Get page context to detect navigation changes
  const pageContext = usePageContext();
  const navigationKey = `${pageContext?.urlPathname || ''}_${pageContext?.urlOriginal || ''}`;
  const prevNavigationKeyRef = useRef<string>(navigationKey);

  useEffect(() => {
    if (!location) {
      console.log('[useLocationTrends] No location provided, clearing data');
      setData(null);
      setIsLoading(false);
      prevLocationRef.current = location;
      prevNavigationKeyRef.current = navigationKey;
      return;
    }

    // Check if location, locale, or navigation has changed
    const locationChanged = prevLocationRef.current !== location;
    const localeChanged = prevLocaleRef.current !== locale;
    const navigationChanged = prevNavigationKeyRef.current !== navigationKey;
    
    // Update refs
    prevLocationRef.current = location;
    prevLocaleRef.current = locale;
    prevNavigationKeyRef.current = navigationKey;

    // Always fetch from API when location, locale, or navigation changes
    // This ensures we get fresh data on client-side navigation
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        // Omit breakdowns for location header; avoids expensive Scan. Use ?breakdowns=true when needed (e.g. salary filter by title).
        const params = new URLSearchParams({
          location,
          locale,
        });

        // Always use the remote API base URL so local development can fetch the data
        const API_BASE_URL = ENV_CONFIG.URLS.PROD_URL;
        const url = `${API_BASE_URL}/api/salary-stats?${params.toString()}`;
        console.log('[useLocationTrends] Fetching location trends from API:', { 
          location, 
          locale, 
          url, 
          locationChanged, 
          localeChanged,
          navigationChanged,
          navigationKey,
          hadInitialData: !!initialData 
        });

        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        });

        console.log('[useLocationTrends] Response status:', response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text().catch(() => response.statusText);
          console.error('[useLocationTrends] Fetch failed:', { status: response.status, statusText: response.statusText, errorText });
          throw new Error(`Failed to fetch location trends: ${response.statusText}`);
        }

        const result = await response.json() as LocationTrendsData;
        
        console.log('[useLocationTrends] Fetch successful:', {
          location,
          locale,
          hasSalaryStats: !!result.salaryStats,
          hasTrends: !!result.trends,
          trendsKeys: result.trends ? Object.keys(result.trends) : [],
          companyDistributionCount: result.trends?.companyDistribution ? Object.keys(result.trends.companyDistribution).length : 0,
          languageRequirementsCount: result.trends?.languageRequirements ? Object.keys(result.trends.languageRequirements).length : 0,
          remotenessDistributionCount: result.trends?.remotenessDistribution ? Object.keys(result.trends.remotenessDistribution).length : 0,
        });
        
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('[useLocationTrends] Error fetching location trends:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
          setIsLoading(false);
          // On error, keep existing data (or initialData) rather than clearing it
          // Use functional update to access current state
          setData((currentData) => {
            if (!currentData && initialData) {
              return initialData;
            }
            return currentData;
          });
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [location, locale, navigationKey]); // Added navigationKey to ensure fetch on navigation - removed initialData to avoid double fetches

  // Sync state when initialData changes (from SSR data updates) - but don't trigger fetch
  useEffect(() => {
    if (initialData && !isLoading) {
      // Only update if we don't have data or if initialData is newer
      setData((currentData) => {
        // If we have no current data, use initialData
        if (!currentData) {
          return initialData;
        }
        // If initialData has newer lastUpdated, use it
        const currentLastUpdated = currentData?.salaryStats?.aggregate?.lastUpdated || 0;
        const initialLastUpdated = initialData?.salaryStats?.aggregate?.lastUpdated || 0;
        if (initialLastUpdated > currentLastUpdated) {
          return initialData;
        }
        return currentData;
      });
    }
  }, [initialData, isLoading]);

  return { data, isLoading, error };
}
